import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Nav } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { getPersonData, updatePerson, getOtherOldId } from '../../services/Person/personService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import DocumentInput from '../Utils/DocumentInput';
import PhoneInput from '../Utils/PhoneInput';
import ZipCodeInput from '../Utils/ZipCodeInput';
import InputField from '../Utils/InputField';
import UFSelect from '../Utils/UFSelect';
import LicensePlateInput from '../Utils/LicensePlateInput';
import DecimalInput from '../Utils/DecimalInput';
import { FiRefreshCcw } from 'react-icons/fi';
import { usePermissions } from '../../hooks/usePermissions';

const EditPersonModal = ({ show, handleClose, person, refreshPersons }) => {
  const { register, handleSubmit, setValue, formState: { errors }, reset, watch, control } = useForm();
  const { hasPermission } = usePermissions();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeKey, setActiveKey] = useState('general');
  const [modalKey, setModalKey] = useState(0);
  const isActive = watch('active');
  const canDelete = hasPermission('personDelete');

  useEffect(() => {
    const fetchPersonData = async () => {
      try {
        if (person?.id) {
          const data = await getPersonData(person.id);

          setValue('oldId', data?.oldId);
          setValue('name', data.name);
          setValue('document', data.document);
          setValue('registration', data.registration);
          setValue('businessPhone', data.businessPhone);
          setValue('cellPhone', data.cellPhone);
          setValue('mail', data.mail);
          setValue('licensePlate', data.licensePlate);
          setValue('address', data.address);
          setValue('district', data.district);
          setValue('city', data.city);
          setValue('state', data.state);
          setValue('observations', data.observations);
          setValue('discount', data.discount);
          setValue('isCustomer', true);
          setValue('isSupplier', true);
          setValue('isDriver', true);
          setValue('isProducer', true);
          setValue('active', data?.active);

          const cleanedZip = data.zipCode.replace(/\D/g, '').padEnd(8, '0');
          const formatted = cleanedZip.replace(/^(\d{5})(\d{3})$/, '$1-$2');
          setValue('zipCode', formatted);
        }
      } catch (error) {
        setErrorMessage(error.message || 'Erro ao buscar dados da persona.');
        setShowErrorModal(true);
      }
    };

    if (show && person) {
      fetchPersonData();
    }
    else if (!show) {
      reset();
      setModalKey((prevKey) => prevKey + 1);
    }
  }, [show, person, setValue, reset]);

  const onSubmit = async (data) => {
    const payload = {
      ...data
    };

    try {
      await updatePerson(person.id, payload);
      setSuccessMessage('Pessoa atualizada com sucesso!');
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao atualizar pessoa.');
      setShowErrorModal(true);
    }
  };

  const handleIconClick = async () => {
    try {
      const response = await getOtherOldId();
      if(response) {
        setValue('oldId', response);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshPersons();
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Pessoa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="general">Informações Gerais</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="address">Endereço</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="general">
                  <Row>
                    <Col md={2}>
                      <div className="input-with-icon">
                        <InputField
                          label="Código"
                          name="oldId"
                          register={register}
                          readOnly={true}
                        />

                        <span className="input-icon" onClick={handleIconClick} title="Atualizar código">
                          <FiRefreshCcw size={18} />
                        </span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Nome"
                        name="name"
                        placeholder="Digite o nome"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Nome é obrigatório' }}
                      />
                    </Col>
                    <Col md={4}>
                      <DocumentInput
                        key={modalKey}
                        label="Documento"
                        name="document"
                        register={register}
                        errors={errors}
                        value={watch('document')}
                        isActive={isActive}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3}>
                      <InputField
                        label="Matrícula"
                        name="registration"
                        placeholder="Digite a matrícula"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                      <PhoneInput
                        key={modalKey}
                        label="Telefone Comercial"
                        name="businessPhone"
                        register={register}
                        errors={errors}
                        value={watch('businessPhone')}
                      />
                    </Col>
                    <Col md={3}>
                      <PhoneInput
                        key={modalKey}
                        label="Telefone Celular"
                        name="cellPhone"
                        register={register}
                        errors={errors}
                        value={watch('cellPhone')}
                      />
                    </Col>
                    <Col md={3}>
                      <Controller
                        name="discount"
                        control={control}
                        render={({ field, fieldState }) => (
                          <DecimalInput
                            value={field.value}
                            onChange={field.onChange}
                            label="Taxa de Serviço %"
                            placeholder=" "
                            isInvalid={!!fieldState.error}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <InputField
                        label="Email"
                        name="mail"
                        placeholder="Digite o email"
                        register={register}
                        errors={errors}
                        rules={{
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Endereço de email inválido',
                          }
                        }}
                      />
                    </Col>
                    <Col md={3}>
                      <LicensePlateInput
                        label="Placa do Veículo"
                        name="licensePlate"
                        register={register}
                        errors={errors}
                        placeholder="Placa"
                        value={watch('licensePlate') || ''}
                      />
                    </Col>
                    <Col md={5}>
                      <InputField
                        label="Observações"
                        name="observations"
                        placeholder="Digite observações"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row className='mt-2'>
                    <Col md={12}>
                      <Form.Label className="center">Status</Form.Label>
                      <Controller
                        name="active"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Form.Check
                            type="checkbox"
                            label="Ativo"
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={!canDelete}
                          />
                        )}
                      />
                    </Col>   
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="address">
                  <Row>
                    <Col md={6}>
                      <InputField
                        label="Endereço"
                        name="address"
                        placeholder="Digite o endereço"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Bairro"
                        name="district"
                        placeholder="Digite o bairro"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={5}>
                      <InputField
                        label="Cidade"
                        name="city"
                        placeholder="Digite a cidade"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <UFSelect
                        label="Estado"
                        name="state"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                      <ZipCodeInput
                        label="CEP"
                        name="zipCode"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit">
              Atualizar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default EditPersonModal;